import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  IS_MULTILINGUAL,
} from '@/config/site';
import { localizedPath, parseLocalizedPath } from '@/lib/localizedPath';
import { getLocaleMeta } from '@/i18n/locale-meta';
import api from '@/lib/api';

/**
 * Map a public route to its slug-translation type so we can ask the backend
 * for the target-locale slug. Order matters — first match wins. Routes that
 * aren't here (Home, Shop list, Cart, etc.) just swap the locale prefix and
 * keep the same path; that's correct because nothing in their URL is
 * locale-dependent.
 *
 * We deliberately omit `/shop/:category` slugs from translation for now —
 * categories support translation in the DB, but the wizard isn't yet
 * generating non-default-locale category slugs across tenants. When that
 * lands (Phase 1f backfill), add `{re: /^\/shop\/[^/]+$/, type: 'category'}`.
 */
const SLUG_ROUTES: Array<{ re: RegExp; type: string }> = [
  { re: /^\/product\/([^/]+)$/, type: 'product' },
  { re: /^\/blog\/([^/]+)$/, type: 'blog_post' },
  { re: /^\/page\/([^/]+)$/, type: 'page' },
];

interface ResolveSlugResponse {
  slug: string | null;
}

function listingFallback(type: string): string {
  switch (type) {
    case 'product': return '/shop';
    case 'blog_post': return '/blog';
    default: return '/';
  }
}

/**
 * Header dropdown for switching locale.
 *
 * Single-locale tenants render nothing (the switcher hides itself). For
 * multilingual tenants, we render a `<select>` because:
 *   - it's natively keyboard accessible (no extra ARIA wiring needed)
 *   - it works on every browser without JS framework gymnastics
 *   - it ports cleanly to mobile (uses the OS picker)
 *
 * Custom-styled dropdowns can replace this later for design polish.
 */
export default function LocaleSwitcher({ className }: { className?: string }) {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  const [busy, setBusy] = useState(false);

  if (!IS_MULTILINGUAL) return null;

  const current = i18n.language || DEFAULT_LOCALE;

  async function changeLocale(target: string) {
    if (target === current || busy) return;
    setBusy(true);
    try {
      const { path: bareWithoutLocale } = parseLocalizedPath(pathname);

      // Try slug-bearing route mapping first.
      let nextPath = bareWithoutLocale;
      for (const { re, type } of SLUG_ROUTES) {
        const m = bareWithoutLocale.match(re);
        if (!m) continue;
        const slug = m[1];
        try {
          const resp = await api.get<ResolveSlugResponse>(
            `/i18n/resolve-slug?type=${encodeURIComponent(type)}&fromLocale=${encodeURIComponent(current)}&toLocale=${encodeURIComponent(target)}&slug=${encodeURIComponent(slug)}`,
          );
          if (resp?.slug) {
            // Replace the slug in the path. m.index is the start of the slug match.
            nextPath = bareWithoutLocale.replace(slug, resp.slug);
          } else {
            // No translated slug exists — bounce to the listing page so the
            // user lands on a working URL rather than a 404.
            nextPath = listingFallback(type);
          }
        } catch {
          // Resolver errors shouldn't trap the user on the wrong locale.
          nextPath = listingFallback(type);
        }
        break;
      }

      const localized = localizedPath(nextPath, target);
      // i18next's path detector reads the URL; navigate first, then change
      // language so the LocaleResolver in App.tsx picks it up consistently.
      navigate(`${localized}${search}${hash}`, { replace: false });
      void i18n.changeLanguage(target);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className={className}>
      <span className="sr-only">{t('languageSwitcher.ariaLabel')}</span>
      <span className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border border-sage-200 bg-cream text-sage-800 text-sm focus-within:ring-2 focus-within:ring-sage-600">
        <Globe className="h-4 w-4" aria-hidden="true" />
        <select
          aria-label={t('languageSwitcher.ariaLabel')}
          value={current}
          onChange={(e) => void changeLocale(e.target.value)}
          disabled={busy}
          className="bg-transparent pr-1 text-sm focus:outline-none disabled:opacity-50"
        >
          {SUPPORTED_LOCALES.map((loc) => (
            <option key={loc} value={loc}>
              {getLocaleMeta(loc).nativeName}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}
